import React, {Fragment} from "react";
import {useTranslation} from "react-i18next";
import styled from "@emotion/styled";
import {Modal} from "@mui/material";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {useIsMobile} from "data/hooks";
import {ModalCloseButton, ModalContent} from "views/components/modals/common";
import {PrimaryButton, SecondaryButton} from "views/components/common";
import {ICouponModalController} from "./coupon_modal.controller";
import {ReactComponent as IconClose} from "assets/img/icons/close.svg";
import {ReactComponent as ChillyMoose} from "assets/img/couponModal/chillyMoose.svg";
import logo from "assets/img/couponModal/logo.png";
import bg from "assets/img/couponModal/bg.png";
import couponBg from "assets/img/couponModal/couponBg.png";
import couponBgMobile from "assets/img/couponModal/couponBgMobile.png";

const SModalContent = styled(ModalContent)`
	@media (max-height: 900px) {
		padding: 16px;
	}

	@media (max-height: 750px) {
		padding: 8px;
	}
`;

const Container = styled.div`
	width: 100%;
	max-width: 448px;
	display: flex;
	flex-direction: column;
`;

const Icons = styled.div`
	height: 88px;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 20px;

	@media (min-width: 769px) {
		height: 140px;
		margin-bottom: 28px;
	}

	img,
	svg {
		width: auto;
		height: 100%;
	}

	@media (max-height: 900px) {
		max-height: 120px;
	}

	@media (max-height: 750px) {
		max-height: 80px;
	}
`;

const ModalTitle = styled.div`
	text-align: center;
	font-size: 20px;
	font-weight: 700;
	line-height: 135%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	margin-bottom: 12px;

	@media (min-width: 769px) {
		font-size: 24px;
		margin-bottom: 16px;
	}

	@media (max-height: 900px) {
		font-size: 20px;
	}

	@media (max-height: 800px) {
		font-size: 18px;
	}

	@media (max-height: 750px) {
		font-size: 14px;
	}
`;

const Content = styled.div`
	background: url(${bg}) no-repeat bottom center;
	background-size: cover;
`;

const Coupon = styled.div`
  display: flex;
  flex-direction: column;
  background: #313131;
  position: relative;

  @media (min-width: 769px) {
    &:before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(270deg, rgba(40, 40, 40, 0.00) 0%, #282828 73.19%);
    }
`;

const CouponImage = styled.img`
	width: 100%;
`;

const CouponTitle = styled.div`
	font-size: 48px;
	font-weight: 700;
	line-height: 100%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	padding: 12px 16px;

	.small {
		font-size: 18px;
		line-height: 120%;
	}

	.normal {
		font-size: 20px;
		line-height: 120%;
	}

	@media (min-width: 769px) {
		padding: 12px;
		position: absolute;
		left: 0;
		top: 0;
		width: 63%;
		z-index: 1;
		font-size: 60px;

		.small {
			font-size: 20px;
		}

		.normal {
			font-size: 24px;
		}
	}

	@media (max-height: 900px) and (max-width: 768px) {
		font-size: 40px;

		.small {
			font-size: 14px;
		}

		.normal {
			font-size: 16px;
		}
	}

	@media (max-height: 800px) and (max-width: 768px) {
		font-size: 30px;
		padding: 8px 12px;
	}
`;

const CouponDesc = styled.div`
	font-family: "Hind", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 125%;
	letter-spacing: 0.28px;
	text-transform: initial;
	margin-top: 8px;

	@media (min-width: 769px) {
		font-size: 16px;
	}
`;

const CouponInfo = styled.div`
	padding: 16px;

	@media (min-width: 769px) {
		padding: 20px;
	}

	@media (max-height: 800px) {
		padding: 8px;
	}
`;

const CouponInfoTitle = styled.div`
	text-align: center;
	font-size: 18px;
	font-weight: 700;
	line-height: 120%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	margin-bottom: 8px;

	@media (min-width: 769px) {
		font-size: 20px;
	}

	@media (max-height: 800px) {
		font-size: 16px;
	}
`;

const CouponCode = styled.div`
	border: 1px solid #f6f6f6;
	border-radius: 4px;
	text-align: center;
	font-size: 32px;
	font-weight: 700;
	line-height: 124%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	padding: 4px;
	margin-bottom: 12px;

	@media (min-width: 769px) {
		font-size: 44px;
	}

	@media (max-height: 800px) {
		font-size: 24px;
		margin-bottom: 8px;
	}
`;

const Buttons = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	width: 100%;
	gap: 12px;
	margin-bottom: 12px;

	button {
		padding: 12px 4px;
		font-size: 14px;

		@media (min-width: 769px) {
			font-size: 16px;
		}
	}

	@media (max-height: 800px) {
		margin-bottom: 8px;

		button {
			height: 40px;
		}
	}
`;

const Disclaimer = styled.div`
	text-align: center;
	font-family: "Hind", sans-serif;
	font-size: 12px;
	font-weight: 500;
	line-height: 132%;
	letter-spacing: 0.24px;
	cursor: pointer;
`;

const DisclaimerTitle = styled.div`
	text-align: center;
	font-size: 24px;
	font-weight: 700;
	line-height: 135%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	margin-top: 10px;
`;

const DisclaimerText = styled.span`
	font-family: "Hind", sans-serif;
`;

export const CouponModal: React.FC = observer(() => {
	const {isOpen, close, coupon, copyCode, code, couponUrl, toggleDisclaimer, isShowDisclaimer} =
		useViewController<ICouponModalController>(Bindings.CouponModalController);
	const {t} = useTranslation();
	const disclaimerText = t("picks.disclaimer_modal.text", "");
	const {isMobile} = useIsMobile("(max-width: 768px)");
	const couponImage = isMobile ? couponBgMobile : couponBg;
	const title = t(
		"picks.coupon_modal.title",
		"To celebrate, enjoy 25% off your Chilly Moose online order, including coolers, drinkware & gear!"
	);

	if (!coupon) {
		return null;
	}

	return (
		<Fragment>
			<Modal open={isOpen} onClose={close}>
				<SModalContent>
					<Container>
						<ModalCloseButton onClick={close}>
							<IconClose />
						</ModalCloseButton>

						<Icons>
							<img src={logo} alt="" />
							<ChillyMoose />
						</Icons>

						{/*Disabled due html come from Loco and should be safety*/}
						{/*eslint-disable-next-line*/}
						<ModalTitle dangerouslySetInnerHTML={{__html: title}} />

						<Content>
							<Coupon>
								<CouponImage src={couponImage} />

								<CouponTitle>
									<div className="normal">
										{t(
											"picks.coupon_modal.coupon_text_top",
											"To celebrate, enjoy"
										)}
									</div>

									<div>
										{t("picks.coupon_modal.coupon_text_middle", "25% off")}
									</div>

									<div className="small">
										{t(
											"picks.coupon_modal.coupon_text_bottom",
											"your Chilly Moose online order, including coolers, drinkware & gear!"
										)}
									</div>

									<CouponDesc>
										{t(
											"picks.coupon_modal.coupon_desc",
											"Chilly Moose. Cold should stay cold, Hot should stay hot."
										)}
									</CouponDesc>
								</CouponTitle>
							</Coupon>

							<CouponInfo>
								<CouponInfoTitle>
									{t("picks.coupon_modal.coupon_title", "YOUR COUPON CODE")}
								</CouponInfoTitle>

								<CouponCode>{code}</CouponCode>

								<Buttons>
									<a href={couponUrl} download>
										<PrimaryButton fullWidth>
											{t("picks.coupon_modal.download", "Download Coupon")}
										</PrimaryButton>
									</a>

									<SecondaryButton fullWidth onClick={copyCode}>
										{t("picks.coupon_modal.copy", "COPY COUPON CODE")}
									</SecondaryButton>
								</Buttons>

								<Disclaimer onClick={toggleDisclaimer}>
									{t(
										"picks.coupon_modal.disclaimer",
										"Valid until December 31st 2024. Must be age of majority in province of residence"
									)}
								</Disclaimer>
							</CouponInfo>
						</Content>
					</Container>
				</SModalContent>
			</Modal>

			<Modal open={isShowDisclaimer} onClose={toggleDisclaimer}>
				<ModalContent>
					<Container>
						<ModalCloseButton onClick={toggleDisclaimer}>
							<IconClose />
						</ModalCloseButton>

						<DisclaimerTitle>
							{t("picks.disclaimer_modal.title", "Terms & Conditions")}
						</DisclaimerTitle>

						{/*Disabled due html come from Loco and should be safety*/}
						{/*eslint-disable-next-line*/}
						<DisclaimerText dangerouslySetInnerHTML={{__html: disclaimerText}} />
					</Container>
				</ModalContent>
			</Modal>
		</Fragment>
	);
});
